import { inject } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert } from '@sama/js-core';
import { Subject } from 'rxjs';
import { ShapeLocationState2D } from '../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import {
  getShapeForEventTracking,
  ShapeState2D,
} from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { TrackingEvent } from '../../../../cross-team-shared/event-tracking/event-utils';
import { VectorTaggingToolObjectless } from '../../../../cross-team-shared/tools-names';
import { EventTrackingValue } from '../../../../team-apis/core/event-tracking';
import {
  Point2D,
  ShapeLocation,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { AbstractSensorContentState2D } from '../../state/abstract-sensor-content-state-2d';
import { AbstractSensorContentStore2D } from '../../state/abstract-sensor-content-store-2d';
import { ExtrapolationService } from '../../utils/extrapolation/extrapolation.service';
import { ensureKeyLocation, LocationHelper } from '../../utils/location-helper';
import { AbstractTool2D } from './abstract-tool-2d';
import { ShapeTool } from './tool-helpers/abstract-shape-tool-helper';

/**
 * In general a shape tool handles things when shape draw is finished such as
 * adding shape object to the shape store.
 */
export abstract class AbstractShapeTool2D
  extends AbstractTool2D<
    AbstractSensorContentState2D,
    AbstractSensorContentStore2D
  >
  implements ShapeTool
{
  drawShapeFinished$ = new Subject<void>();
  private extrapolationService = inject(ExtrapolationService);
  private redrawShapeStore?: Store<ShapeState2D>;
  private shapeBeforeRedraw?: ShapeState2D;

  abstract override name: VectorTaggingToolObjectless;

  startRedraw(shapeStore: Store<ShapeState2D>) {
    this.shapeBeforeRedraw = shapeStore.state();
    this.redrawShapeStore = shapeStore;
    shapeStore('key_locations').set([]);
  }

  notifyDrawFinished(shape: ShapeState2D | undefined) {
    if (this.redrawShapeStore) {
      this.updateShape(shape);
    } else if (shape) {
      this.addShape(shape);
    }
    this.drawShapeFinished$.next();
  }

  override onCancel(eventTrackingValue: EventTrackingValue) {
    super.onCancel(eventTrackingValue);
    this.notifyDrawFinished(undefined);
  }

  buildFirstKeyLocation(initialValues: ShapeLocation<Point2D>) {
    return { ...initialValues, points: [this.getSnappedImagePoint()] };
  }

  buildShape(initialValues: ShapeState2D) {
    return initialValues;
  }

  private updateShape(shape: ShapeState2D | undefined) {
    const redrawShapeStore = this.redrawShapeStore;
    const shapeBeforeRedraw = this.shapeBeforeRedraw;
    assert(
      redrawShapeStore && shapeBeforeRedraw,
      'This should only be called when redrawing',
    );

    this.store.batch(() => {
      redrawShapeStore('key_locations').set(shapeBeforeRedraw.key_locations);
      if (shape) {
        this.setRedrawnKeyLocation(this.store, shape.key_locations[0]);
        this.shellConnector.setShapes(this.store.state());
      }
      this.shellConnector.modifyShapeSelection([shapeBeforeRedraw.index]);
    });

    if (shape) {
      this.handlePushAndTrack(
        { ...shape, index: shapeBeforeRedraw.index },
        {
          id: 'edit_shape_points',
          bucket: 'adjustment',
          method: 'redraw',
          source: 'edit_tool',
        },
      );
    }
    this.shapeBeforeRedraw = undefined;
    this.redrawShapeStore = undefined;
  }

  private addShape(shape: ShapeState2D) {
    this.shellConnector.createShape(shape);
    this.extrapolationService.updateExtrapolation(
      shape.index,
      this.store.state().frameNumber,
    );
  }

  private handlePushAndTrack(shape: ShapeState2D, event: TrackingEvent) {
    const framesCount = this.extrapolationService.updateExtrapolation(
      shape.index,
      this.store.state().frameNumber,
    );
    const shapeEventData = {
      ...getShapeForEventTracking(shape),
      ...(framesCount && {
        requested_extrapolated_frames_count: framesCount,
      }),
    };

    this.shellConnector.pushAndTrack({
      ...event,
      shapes: [shapeEventData],
    });
  }

  private setRedrawnKeyLocation(
    store: AbstractSensorContentStore2D,
    location: ShapeLocationState2D,
  ) {
    assert(this.redrawShapeStore, 'This should only be called when redrawing');

    const { frameNumber } = this.store.state();
    const locationStore = ensureKeyLocation(
      new LocationHelper(store),
      this.redrawShapeStore('key_locations'),
      frameNumber,
    );
    locationStore.set(location);
  }
}
